/**
 * Tiny ergonomic wrapper over the project-wide axios instance.
 *
 * The Pharma SFA module's pages were authored to talk to the backend
 * via two functions — ``getJSON(path)`` and ``postJSON(path, body)``
 * — which return parsed JSON and rethrow with a friendly ``.message``
 * on failure.  Rather than rewrite every Pharma page to call the
 * existing ``axios`` instance directly (and re-discover its quirks
 * around tokens, tenant headers, 401 redirects, etc.), this module
 * adapts that instance to the simpler contract.
 *
 * The underlying ``api`` instance — defined in ``./axios.js`` —
 * already:
 *   * Attaches the JWT bearer token from ``localStorage`` on each
 *     request.
 *   * Refreshes expired access tokens automatically.
 *   * Routes 401s back to ``/login`` so a stale tab can't keep
 *     hitting the backend with a dead token.
 *
 * Existing Tasks / Complaints / Chat / Business pages keep using
 * ``api`` directly.  This file is purely additive.
 */
import api from './axios.js';


/**
 * Normalise the path argument before handing it to axios.
 *
 * Background
 * ----------
 * The shared ``api`` instance in ``./axios.js`` is configured with
 * ``baseURL = window.location.origin + '/api'``.  Axios always
 * concatenates the request URL onto ``baseURL`` for relative paths,
 * which means a caller that passes ``"/api/uni-email/contacts/"`` ends
 * up requesting ``"<origin>/api/api/uni-email/contacts/"`` — Django
 * has no such route, so the SPA gets a 404 page instead of JSON.
 *
 * The Pharma SFA and Uni-* pages were authored expecting the
 * ``baseURL`` to be the bare origin (i.e. they hand-roll the leading
 * ``/api/``).  Rather than rewrite ~30 page files, we strip a single
 * leading ``/api`` here so both styles work:
 *
 *   * ``getJSON('/api/uni-email/contacts/')`` → ``/api/uni-email/contacts/``
 *   * ``getJSON('/uni-email/contacts/')``     → ``/api/uni-email/contacts/``
 *   * ``getJSON('uni-email/contacts/')``      → ``/api/uni-email/contacts/``
 *   * ``getJSON('https://other.example/x')``  → unchanged (absolute URL)
 *
 * Strict / single-replace so we never accidentally chew off ``/api``
 * twice on a path like ``/api/api/x/`` (which shouldn't occur, but
 * costs us nothing to be careful).
 */
function normalisePath(path) {
  if (typeof path !== 'string') return path;
  // Absolute URLs (http://, https://, //cdn.example) bypass baseURL
  // anyway — leave them untouched.
  if (/^[a-z][a-z0-9+.-]*:/i.test(path) || path.startsWith('//')) {
    return path;
  }
  // Strip exactly one leading "/api" or "api" prefix when present.
  if (path.startsWith('/api/'))  return path.slice(4);   // "/api/foo" -> "/foo"
  if (path === '/api')           return '/';
  if (path.startsWith('api/'))   return '/' + path.slice(4); // "api/foo" -> "/foo"
  if (path === 'api')            return '/';
  // Make sure we always hand axios a leading slash so it concatenates
  // onto baseURL cleanly ("/api" + "/foo/" = "/api/foo/").
  if (!path.startsWith('/'))     return '/' + path;
  return path;
}


function unwrapError(err) {

  if (err && err.response) {
    const { status, data } = err.response;
    const detail = (data && (data.detail || data.error || data.message)) || JSON.stringify(data);
    const e = new Error(`HTTP ${status}: ${detail}`);
    e.status = status;
    e.data = data;
    return e;
  }
  return err instanceof Error ? err : new Error(String(err));
}


export async function getJSON(path, config) {
  try {
    const res = await api.get(normalisePath(path), config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}


export async function postJSON(path, body, config) {
  try {
    const res = await api.post(normalisePath(path), body || {}, config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}


export async function patchJSON(path, body, config) {
  try {
    const res = await api.patch(normalisePath(path), body || {}, config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}


export async function putJSON(path, body, config) {
  try {
    const res = await api.put(normalisePath(path), body || {}, config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}


export async function deleteJSON(path, config) {
  try {
    const res = await api.delete(normalisePath(path), config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}



/**
 * Download an endpoint's response as a file and hand it to the browser.
 *
 * Used by the Pharma master screens for "Download sample Excel" and
 * "Export to Excel".  A plain ``<a href>`` cannot be used for these:
 * the endpoints require the ``Authorization`` header that
 * ``./axios.js`` attaches, and a browser navigation sends no such
 * header — the user would get a 401 HTML page saved as a .xlsx.
 *
 * So we fetch the bytes through axios (auth intact), wrap them in an
 * object URL and click a synthetic link.  ``revokeObjectURL`` runs in a
 * ``finally`` so the blob is released even if the click throws.
 *
 * ``fallbackName`` is used when the response carries no
 * ``Content-Disposition`` filename, which is the normal case for our
 * endpoints since they set it explicitly but proxies sometimes strip it.
 */
export async function downloadFile(path, fallbackName = 'download.xlsx') {
  let url;
  try {
    const res = await api.get(normalisePath(path), { responseType: 'blob' });

    // Prefer the server's filename when the header survived the trip.
    let filename = fallbackName;
    const disp = res.headers?.['content-disposition'] || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disp);
    if (match) filename = decodeURIComponent(match[1]);

    url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    // Firefox needs the node in the document for programmatic clicks.
    document.body.appendChild(a);
    a.click();
    a.remove();
    return filename;
  } catch (err) {
    // An error response to a blob request arrives AS a blob, so the
    // usual ``err.response.data.detail`` is unreadable.  Convert it
    // back to text so the user sees the real message instead of
    // "[object Blob]".
    if (err?.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        try {
          const parsed = JSON.parse(text);
          err.response.data = parsed;
        } catch {
          err.response.data = { detail: text.slice(0, 300) };
        }
      } catch { /* keep the original error */ }
    }
    throw unwrapError(err);
  } finally {
    if (url) window.URL.revokeObjectURL(url);
  }
}


/**
 * POST a single file as ``multipart/form-data``.
 *
 * The Content-Type is deliberately NOT set by hand: the browser must
 * generate it so it can include the MIME boundary token.  Setting it
 * manually is the classic cause of "no file was submitted" errors.
 *
 * ``extra`` adds scalar form fields alongside the file (e.g.
 * ``{ price_list: 3 }`` when importing price-list rows).
 */
export async function uploadFile(path, file, extra = {}, config = {}) {
  const form = new FormData();
  form.append('file', file);
  Object.entries(extra).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') form.append(k, v);
  });
  try {
    const res = await api.post(normalisePath(path), form, config);
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}


export default { getJSON, postJSON, patchJSON, deleteJSON, downloadFile, uploadFile };
